import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../contexts/AuthContext';
import useResponsive from '../hooks/useResponsive';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Route as RouteIcon,
  Map as MapIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { BACKEND_BASE_URL } from '../config';

// Utility function to format dates in Indian format (DD/MM/YYYY)
const formatIndianDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Add this style for Leaflet attribution
const mapStyle = `
  .leaflet-control-attribution {
    display: none !important;
  }
  .leaflet-container {
    height: 100% !important;
    width: 100% !important;
  }
`;

function KPICard({ title, value, icon, height }) {
  const { isSmallMobile, isMobile } = useResponsive();

  // Responsive height calculation
  const getResponsiveHeight = () => {
    if (isSmallMobile) return height ? Math.min(parseInt(height), 80) + 'px' : '80px';
    if (isMobile) return height ? Math.min(parseInt(height), 100) + 'px' : '100px';
    return height || '120px';
  };

  // Responsive padding
  const getResponsivePadding = () => {
    if (isSmallMobile) return 1;
    if (isMobile) return 1.25;
    return 1.5;
  };

  // Responsive icon size
  const getIconSize = () => {
    if (isSmallMobile) return '1rem';
    if (isMobile) return '1.2rem';
    const numHeight = parseInt(height) || 120;
    return numHeight <= 100 ? '1.2rem' : numHeight <= 120 ? '1.5rem' : '2rem';
  };

  // Responsive title font size
  const getTitleFontSize = () => {
    if (isSmallMobile) return '0.7rem';
    if (isMobile) return '0.75rem';
    const numHeight = parseInt(height) || 120;
    return numHeight <= 100 ? '0.7rem' : numHeight <= 120 ? '0.75rem' : '0.8rem';
  };

  // Responsive value font size
  const getValueFontSize = () => {
    if (isSmallMobile) return '0.875rem';
    if (isMobile) return '1rem';
    const numHeight = parseInt(height) || 120;
    return numHeight <= 100 ? '0.9rem' : numHeight <= 120 ? '1rem' : '1.1rem';
  };

  return (
    <Card sx={{
      height: getResponsiveHeight(),
      minHeight: isSmallMobile ? '60px' : '80px'
    }}>
      <CardContent sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: getResponsivePadding(),
        '&:last-child': { pb: getResponsivePadding() }
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          {icon && (
            <Box sx={{
              mr: isSmallMobile ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              '& svg': {
                fontSize: getIconSize()
              }
            }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: getTitleFontSize(),
              lineHeight: 1.2,
              wordBreak: 'break-word',
              hyphens: 'auto'
            }}
            className="responsive-text"
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          mt: isSmallMobile ? 0.5 : 1
        }}>
          <Typography
            variant="h6"
            sx={{
              color: '#F79233',
              fontSize: getValueFontSize(),
              fontWeight: 'bold',
              textAlign: 'center',
              width: '100%',
              lineHeight: 1.1,
              wordBreak: 'break-word'
            }}
            className="responsive-text"
          >
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function FeatureStatusChart({ corridorId, project }) {
  const [workStatus, setWorkStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openGallery, setOpenGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const { isSmallMobile, isMobile } = useResponsive();

  useEffect(() => {
    const fetchWorkStatus = async () => {
      try {
        const encodedCorridorId = encodeURIComponent(corridorId);
        const response = await axios.get(`${BACKEND_BASE_URL}/api/field-officer/work-status/${project._id}/${encodedCorridorId}`);
        setWorkStatus(response.data);
      } catch (error) {
        console.error('Error fetching work status:', error);
        setWorkStatus(null);
      } finally {
        setLoading(false);
      }
    };

    if (corridorId && project?._id) {
      fetchWorkStatus();
    }
  }, [corridorId, project]);

  const getStatusColor = (updatedDate, deadline) => {
    if (!deadline) return '#808080';
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    
    if (!updatedDate) {
      return today <= deadlineDate ? '#808080' : '#ff0000';
    }
    
    const updateDate = new Date(updatedDate);
    if (updateDate <= deadlineDate) {
      return '#4caf50';
    }
    return '#ffd700';
  };

  const handleImageClick = (images) => {
    if (images && images.length > 0) {
      setSelectedImage({ images, currentIndex: 0 });
      setOpenGallery(true);
    }
  };

  const handleGalleryClose = () => {
    setOpenGallery(false);
    setSelectedImage(null);
  };

  const handleNextImage = () => {
    setSelectedImage(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const handlePrevImage = () => {
    setSelectedImage(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  // Filter features based on document requirements - only show stages with at least one enabled subsection
  const allFeatures = [
    {
      name: 'Site Visit',
      stage: 'site_visit',
      updatedDate: workStatus?.site_visit?.date,
      deadline: project?.stage_deadlines?.site_visit,
      documents: workStatus?.site_visit?.documents ? Object.values(workStatus.site_visit.documents).flat() : []
    },
    {
      name: 'Design Approval',
      stage: 'design_approval',
      updatedDate: workStatus?.design_approval?.date,
      deadline: project?.stage_deadlines?.design_approval,
      documents: workStatus?.design_approval?.documents ? Object.values(workStatus.design_approval.documents).flat() : []
    },
    {
      name: 'Material Delivery',
      stage: 'product_delivery',
      updatedDate: workStatus?.product_delivery?.date,
      deadline: project?.stage_deadlines?.product_delivery,
      documents: workStatus?.product_delivery?.documents ? Object.values(workStatus.product_delivery.documents).flat() : []
    },
    {
      name: 'Installation',
      stage: 'installation',
      updatedDate: workStatus?.installation?.date,
      deadline: project?.stage_deadlines?.installation,
      documents: workStatus?.installation?.documents ? Object.values(workStatus.installation.documents).flat() : []
    }
  ];

  // Filter features based on document requirements
  const features = allFeatures.filter(feature => {
    const stageRequirements = project?.document_requirements?.[feature.stage];
    return stageRequirements && Object.values(stageRequirements).some(Boolean);
  });

  if (loading) {
    return (
      <Card sx={{ mt: 3, height: '200px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>Field Officer Work Status</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#F79233' }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{
        mt: isSmallMobile ? 1 : 3,
        height: isSmallMobile ? 'auto' : isMobile ? '180px' : '200px',
        minHeight: isSmallMobile ? '150px' : '180px'
      }}>
        <CardContent sx={{ p: isSmallMobile ? 1 : 2 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isSmallMobile ? 'flex-start' : 'center',
            mb: isSmallMobile ? 1 : 2,
            flexDirection: isSmallMobile ? 'column' : 'row',
            gap: isSmallMobile ? 1 : 0
          }}>
            <Typography
              variant={isSmallMobile ? "subtitle1" : "h6"}
              sx={{
                color: '#F79233',
                fontSize: isSmallMobile ? '1rem' : '1.25rem'
              }}
            >
              Field Officer Work Status
            </Typography>
            {/* Color Legend */}
            <Box sx={{
              display: 'flex',
              gap: isSmallMobile ? 0.5 : 1.5,
              flexWrap: 'wrap',
              justifyContent: isSmallMobile ? 'flex-start' : 'flex-end'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: isSmallMobile ? 8 : 10,
                  height: isSmallMobile ? 8 : 10,
                  bgcolor: '#4caf50',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontSize: isSmallMobile ? '0.7rem' : '0.75rem' }}>
                  On Time
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: isSmallMobile ? 8 : 10,
                  height: isSmallMobile ? 8 : 10,
                  bgcolor: '#808080',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontSize: isSmallMobile ? '0.7rem' : '0.75rem' }}>
                  Pending
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: isSmallMobile ? 8 : 10,
                  height: isSmallMobile ? 8 : 10,
                  bgcolor: '#ffd700',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontSize: isSmallMobile ? '0.7rem' : '0.75rem' }}>
                  Delayed
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{
                  width: isSmallMobile ? 8 : 10,
                  height: isSmallMobile ? 8 : 10,
                  bgcolor: '#ff0000',
                  borderRadius: '2px'
                }} />
                <Typography variant="caption" sx={{ fontSize: isSmallMobile ? '0.7rem' : '0.75rem' }}>
                  Overdue
                </Typography>
              </Box>
            </Box>
          </Box>
          <Grid container spacing={isSmallMobile ? 0.5 : isMobile ? 1 : 2}>
            {features.map((feature, index) => (
              <Grid item xs={isSmallMobile ? 6 : 12} sm={6} md={3} key={index}>
                <Box sx={{
                  p: isSmallMobile ? 0.5 : 1,
                  borderRadius: 1,
                  bgcolor: getStatusColor(feature.updatedDate, feature.deadline),
                  color: 'white',
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: isSmallMobile ? '80px' : '100px'
                }}>
                  <Typography
                    variant={isSmallMobile ? "caption" : "subtitle2"}
                    sx={{
                      fontWeight: 'bold',
                      mb: 0.5,
                      fontSize: isSmallMobile ? '0.7rem' : '0.875rem',
                      lineHeight: 1.2
                    }}
                    className="responsive-text"
                  >
                    {feature.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: isSmallMobile ? '0.6rem' : '0.75rem' }}
                  >
                    Deadline: {formatIndianDate(feature.deadline)}
                  </Typography>
                  {feature.updatedDate ? (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: isSmallMobile ? '0.6rem' : '0.75rem' }}
                      >
                        Updated: {formatIndianDate(feature.updatedDate)}
                      </Typography>
                      {feature.documents && feature.documents.length > 0 && (
                        <Box sx={{
                          mt: 0.5,
                          display: 'flex',
                          gap: isSmallMobile ? 0.25 : 0.5,
                          justifyContent: 'center',
                          flexWrap: 'wrap'
                        }}>
                          {feature.documents.slice(0, isSmallMobile ? 2 : 3).map((document, docIndex) => {
                            // Handle both old string format and new S3 object format
                            const documentUrl = typeof document === 'string' ? `${BACKEND_BASE_URL}/uploads/${document}` : document.presigned_url;
                            const filename = typeof document === 'string' ? document : document.filename;

                            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(filename);
                            return (
                              <Box
                                key={docIndex}
                                sx={{
                                  width: isSmallMobile ? '24px' : '40px',
                                  height: isSmallMobile ? '24px' : '40px',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: isImage ? 'transparent' : '#f5f5f5'
                                }}
                                onClick={() => {
                                  if (isImage) {
                                    const imageDocuments = feature.documents.filter(doc => {
                                      const fname = typeof doc === 'string' ? doc : doc.filename;
                                      return /\.(jpg|jpeg|png|gif)$/i.test(fname);
                                    });
                                    handleImageClick(imageDocuments);
                                  } else {
                                    window.open(documentUrl, '_blank');
                                  }
                                }}
                              >
                                {isImage ? (
                                  <img
                                    src={documentUrl}
                                    alt={`${feature.name} ${docIndex + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: '4px'
                                    }}
                                  />
                                ) : (
                                  <Box sx={{ textAlign: 'center' }}>
                                    {filename.toLowerCase().includes('pdf') ? (
                                      <PictureAsPdfIcon sx={{
                                        fontSize: isSmallMobile ? 12 : 16,
                                        color: '#d32f2f'
                                      }} />
                                    ) : (
                                      <DescriptionIcon sx={{
                                        fontSize: isSmallMobile ? 12 : 16,
                                        color: '#1976d2'
                                      }} />
                                    )}
                                  </Box>
                                )}
                                {docIndex === (isSmallMobile ? 1 : 2) && feature.documents.length > (isSmallMobile ? 2 : 3) && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '4px'
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'white',
                                        fontSize: isSmallMobile ? '0.6rem' : '0.75rem'
                                      }}
                                    >
                                      +{feature.documents.length - (isSmallMobile ? 2 : 3)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{ fontSize: isSmallMobile ? '0.6rem' : '0.75rem' }}
                    >
                      Not Updated
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Image Gallery Dialog */}
      <Dialog
        open={openGallery}
        onClose={handleGalleryClose}
        {...(isMobile ? {
          fullScreen: isSmallMobile,
          maxWidth: 'sm',
          fullWidth: true,
          PaperProps: {
            sx: {
              height: isSmallMobile ? '100vh' : '90vh',
              margin: isSmallMobile ? 0 : 2
            }
          }
        } : {
          maxWidth: "md",
          fullWidth: true
        })}
      >
        <DialogContent sx={{
          p: 0,
          position: 'relative',
          height: isSmallMobile ? '100vh' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <IconButton
            onClick={handleGalleryClose}
            sx={{
              position: 'absolute',
              right: isSmallMobile ? 4 : 8,
              top: isSmallMobile ? 4 : 8,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
              zIndex: 1000
            }}
            size={isSmallMobile ? "small" : "medium"}
          >
            <CloseIcon fontSize={isSmallMobile ? "small" : "medium"} />
          </IconButton>
          {selectedImage && (
            <>
              <img
                src={typeof selectedImage.images[selectedImage.currentIndex] === 'string'
                  ? `${BACKEND_BASE_URL}/uploads/${selectedImage.images[selectedImage.currentIndex]}`
                  : selectedImage.images[selectedImage.currentIndex].presigned_url}
                alt={`Image ${selectedImage.currentIndex + 1}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: isSmallMobile ? '100vh' : '80vh',
                  objectFit: 'contain'
                }}
              />
              {selectedImage.images.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: isSmallMobile ? 4 : 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    <NavigateBeforeIcon fontSize={isSmallMobile ? "small" : "medium"} />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: isSmallMobile ? 4 : 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                    }}
                    size={isSmallMobile ? "small" : "medium"}
                  >
                    <NavigateNextIcon fontSize={isSmallMobile ? "small" : "medium"} />
                  </IconButton>
                </>
              )}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: isSmallMobile ? 4 : 8,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: isSmallMobile ? '0.7rem' : '0.75rem'
                }}
              >
                {selectedImage.currentIndex + 1} / {selectedImage.images.length}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CorridorDashboard({ corridor, fieldOfficers, project }) {
  const [routePath, setRoutePath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeItems, setScopeItems] = useState([]);
  const [fieldOfficerAssignments, setFieldOfficerAssignments] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCorridor, setSelectedCorridor] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, fieldOfficerId: null, fieldOfficerName: '' });
  const mapRef = useRef(null);
  const { getToken } = useAuth();
  const { isSmallMobile, isMobile, getContainerPadding, getGridBreakpoints } = useResponsive();

  const center = [
    (parseFloat(corridor.from_lat) + parseFloat(corridor.to_lat)) / 2,
    (parseFloat(corridor.from_lng) + parseFloat(corridor.to_lng)) / 2
  ];

  useEffect(() => {
    fetchRoute();
    fetchScopeItems();
    fetchFieldOfficerAssignment();
  }, [corridor]);

  // Additional useEffect to handle map focusing when route path changes
  useEffect(() => {
    if (routePath.length > 0 && mapRef.current) {
      fitMapToRoute();
    }
  }, [routePath]);

  const fitMapToRoute = () => {
    if (!mapRef.current || routePath.length === 0) return;

    const map = mapRef.current;
    setTimeout(() => {
      try {
        const bounds = routePath.reduce((bounds, coord) => {
          return bounds.extend(coord);
        }, L.latLngBounds(routePath));

        map.fitBounds(bounds, {
          padding: [50, 50], // Add some padding around the bounds
          maxZoom: 15 // Limit maximum zoom level
        });
      } catch (error) {
        console.error('Error fitting map bounds:', error);
      }
    }, 300); // Increased timeout for map recreation
  };

  const fetchRoute = async () => {
    try {
      setLoading(true);
      // Clear previous route path to ensure fresh data
      setRoutePath([]);

      // Use OSRM API to get the actual road route
      const response = await axios.get(
        `https://router.project-osrm.org/route/v1/driving/${corridor.from_lng},${corridor.from_lat};${corridor.to_lng},${corridor.to_lat}?overview=full&geometries=geojson`
      );

      if (response.data.routes && response.data.routes[0]) {
        // Convert coordinates from [lng, lat] to [lat, lng] format for Leaflet
        const coordinates = response.data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
        setRoutePath(coordinates);
      }
    } catch (error) {
      console.error('Error fetching route:', error);
      // Fallback to straight line if route fetching fails
      const fallbackRoute = [
        [parseFloat(corridor.from_lat), parseFloat(corridor.from_lng)],
        [parseFloat(corridor.to_lat), parseFloat(corridor.to_lng)]
      ];
      setRoutePath(fallbackRoute);
    } finally {
      setLoading(false);
    }
  };

  const fetchScopeItems = async () => {
    try {
      // Get scope items from the project's line items
      const scopeItems = project.line_items
        .filter(item => item.corridor_id === corridor.corridor_id)
        .flatMap(item => {
          const items = [];
          if (item.scope_of_work_1 && item.quantity_1) {
            items.push({
              product_type: item.scope_of_work_1,
              quantity: item.quantity_1
            });
          }
          if (item.scope_of_work_2 && item.quantity_2) {
            items.push({
              product_type: item.scope_of_work_2,
              quantity: item.quantity_2
            });
          }
          if (item.scope_of_work_3 && item.quantity_3) {
            items.push({
              product_type: item.scope_of_work_3,
              quantity: item.quantity_3
            });
          }
          return items;
        });
      setScopeItems(scopeItems);
    } catch (error) {
      console.error('Error fetching scope items:', error);
    }
  };

  const fetchFieldOfficerAssignment = async () => {
    try {
      const token = getToken();
      const response = await fetch(
        `${BACKEND_BASE_URL}/api/client/field-officer-assignments/${project._id}/${corridor.corridor_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        setFieldOfficerAssignments(prev => ({
          ...prev,
          [corridor.corridor_id]: data[0].field_officer.name
        }));
      } else {
        setFieldOfficerAssignments(prev => ({
          ...prev,
          [corridor.corridor_id]: null
        }));
      }
    } catch (error) {
      console.error('Error fetching field officer assignment:', error);
    }
  };

  const handleAssignClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAssignClose = () => {
    setAnchorEl(null);
  };

  const handleAssignFieldOfficer = (fieldOfficerId) => {
    const selectedOfficer = fieldOfficers.find(officer => officer._id === fieldOfficerId);
    const currentAssignment = fieldOfficerAssignments[corridor.corridor_id];

    // If there's already an assignment, show confirmation dialog
    if (currentAssignment) {
      setConfirmDialog({
        open: true,
        fieldOfficerId: fieldOfficerId,
        fieldOfficerName: selectedOfficer?.name || 'Unknown Officer'
      });
    } else {
      // No existing assignment, proceed directly
      performAssignment(fieldOfficerId, false);
    }
    setAnchorEl(null);
  };

  const performAssignment = async (fieldOfficerId, isReassignment = false) => {
    try {
      const token = getToken();
      if (!project || !project._id) {
        console.error('Project ID is missing');
        return;
      }

      console.log('Assigning field officer:', {
        corridor_id: corridor.corridor_id,
        field_officer_id: fieldOfficerId,
        project_id: project._id
      });

      const url = isReassignment
        ? `${BACKEND_BASE_URL}/api/client/field-officer-assignments/${project._id}/${corridor.corridor_id}`
        : `${BACKEND_BASE_URL}/api/client/field-officer-assignments`;

      const method = isReassignment ? 'PUT' : 'POST';

      const body = isReassignment
        ? JSON.stringify({ field_officer_id: fieldOfficerId })
        : JSON.stringify({
            corridor_id: corridor.corridor_id,
            field_officer_id: fieldOfficerId,
            project_id: project._id
          });

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: body,
      });

      const data = await response.json();

      if (response.ok) {
        fetchFieldOfficerAssignment();
      } else {
        console.error('Error assigning field officer:', data.error);
      }
    } catch (error) {
      console.error('Error assigning field officer:', error);
    }
  };

  const handleConfirmReassignment = () => {
    performAssignment(confirmDialog.fieldOfficerId, true);
    setConfirmDialog({ open: false, fieldOfficerId: null, fieldOfficerName: '' });
  };

  const handleCancelReassignment = () => {
    setConfirmDialog({ open: false, fieldOfficerId: null, fieldOfficerName: '' });
  };

  const handleDeleteAssignment = async () => {
    if (window.confirm(`Are you sure you want to remove the field officer assignment for corridor ${corridor.corridor_id}?`)) {
      try {
        const token = getToken();
        const response = await fetch(
          `${BACKEND_BASE_URL}/api/client/field-officer-assignments/${project._id}/${corridor.corridor_id}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          fetchFieldOfficerAssignment();
        } else {
          const data = await response.json();
          console.error('Error deleting assignment:', data.error);
        }
      } catch (error) {
        console.error('Error deleting assignment:', error);
      }
    }
    setAnchorEl(null);
  };

  return (
    <Box sx={{
      p: getContainerPadding(),
      height: 'calc(100vh - 80px)',
      overflow: 'auto'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isSmallMobile ? 'flex-start' : 'center',
        mb: isSmallMobile ? 1 : 2,
        flexDirection: isSmallMobile ? 'column' : 'row',
        gap: isSmallMobile ? 1 : 0
      }}>
        <Typography
          variant={isSmallMobile ? "h6" : isMobile ? "h5" : "h5"}
          sx={{
            color: '#F79233',
            fontSize: isSmallMobile ? '1.25rem' : isMobile ? '1.5rem' : '1.5rem',
            wordBreak: 'break-word'
          }}
          className="responsive-text"
        >
          Corridor Analysis: {corridor.corridor_id}
        </Typography>
        <Box sx={{
          display: 'flex',
          gap: isSmallMobile ? 1 : 2,
          pr: isSmallMobile ? 0 : 2,
          width: isSmallMobile ? '100%' : 'auto'
        }}>
          <Button
            variant="contained"
            onClick={handleAssignClick}
            startIcon={!isSmallMobile && <AssignmentIcon sx={{ color: 'white' }} />}
            sx={{
              mt: isSmallMobile ? 0 : 1,
              bgcolor: '#F79233',
              color: 'white',
              fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
              padding: isSmallMobile ? '6px 12px' : '8px 16px',
              flex: isSmallMobile ? 1 : 'none',
              '&:hover': {
                bgcolor: '#F79233',
                opacity: 0.9
              }
            }}
            size={isSmallMobile ? "small" : "medium"}
          >
            {fieldOfficerAssignments[corridor.corridor_id]
              ? (isSmallMobile
                  ? `Assigned: ${fieldOfficerAssignments[corridor.corridor_id]}`
                  : `Assigned: ${fieldOfficerAssignments[corridor.corridor_id]}`)
              : (isSmallMobile ? 'Assign Officer' : 'Assign Field Officer')
            }
          </Button>
        </Box>
      </Box>

      <Grid container spacing={isSmallMobile ? 1 : 2}>
        <Grid item xs={12} md={8}>
          <Card sx={{
            height: isSmallMobile ? '250px' : isMobile ? '280px' : '300px',
            mb: isSmallMobile ? 1 : 2,
            overflow: 'hidden'
          }}>
            <CardContent sx={{ height: '100%', p: 0, position: 'relative' }}>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1000
                  }}
                >
                  <Typography>Loading route...</Typography>
                </Box>
              )}
              <MapContainer
                key={`map-${corridor.corridor_id}-${corridor.from_lat}-${corridor.from_lng}`}
                center={center}
                zoom={isMobile ? 9 : 10}
                style={{
                  height: '100%',
                  width: '100%',
                  touchAction: 'pan-x pan-y'
                }}
                className="map-container"
                zoomControl={!isSmallMobile}
                attributionControl={false}
                whenCreated={(mapInstance) => {
                  mapRef.current = mapInstance;

                  // Add mobile-friendly zoom control
                  if (isSmallMobile) {
                    const zoomControl = L.control.zoom({
                      position: 'topright'
                    });
                    zoomControl.addTo(mapInstance);
                  }

                  // Fit bounds immediately when map is created
                  if (routePath.length > 0) {
                    setTimeout(() => {
                      try {
                        const bounds = routePath.reduce((bounds, coord) => {
                          return bounds.extend(coord);
                        }, L.latLngBounds(routePath));
                        mapInstance.fitBounds(bounds, {
                          padding: isSmallMobile ? [20, 20] : [50, 50],
                          maxZoom: isMobile ? 14 : 15
                        });
                      } catch (error) {
                        console.error('Error fitting map bounds on creation:', error);
                      }
                    }, 100);
                  }
                }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />
                {routePath.length > 0 && (
                  <Polyline
                    positions={routePath}
                    color="#F79233"
                    weight={4}
                    opacity={0.8}
                    smoothFactor={1}
                  />
                )}
                <Marker position={[parseFloat(corridor.from_lat), parseFloat(corridor.from_lng)]}>
                  <Popup>
                    <Typography variant="subtitle2">Start Point</Typography>
                    <Typography variant="body2">Corridor: {corridor.corridor}</Typography>
                  </Popup>
                </Marker>
                <Marker position={[parseFloat(corridor.to_lat), parseFloat(corridor.to_lng)]}>
                  <Popup>
                    <Typography variant="subtitle2">End Point</Typography>
                    <Typography variant="body2">Corridor: {corridor.corridor}</Typography>
                  </Popup>
                </Marker>
              </MapContainer>
            </CardContent>
          </Card>

          <FeatureStatusChart corridorId={corridor.corridor_id} project={project} />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: isSmallMobile ? 0.5 : 1
          }}>
            <KPICard
              title="Corridor"
              value={corridor.corridor}
              icon={<RouteIcon sx={{ color: '#F79233' }} />}
              height={isSmallMobile ? "80px" : isMobile ? "100px" : "140px"}
            />
            <KPICard
              title="Stretch"
              value={`${corridor.stretch_km} KM`}
              icon={<MapIcon sx={{ color: '#F79233' }} />}
              height={isSmallMobile ? "80px" : isMobile ? "100px" : "140px"}
            />
            <Card sx={{ flex: 1, minHeight: isSmallMobile ? '200px' : '250px' }}>
              <CardContent sx={{
                p: isSmallMobile ? 1 : 1.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography
                  variant={isSmallMobile ? "body1" : "subtitle1"}
                  sx={{
                    mb: isSmallMobile ? 0.5 : 1,
                    color: '#F79233',
                    fontSize: isSmallMobile ? '0.875rem' : '1rem'
                  }}
                >
                  Scope of Work
                </Typography>
                <Grid container spacing={isSmallMobile ? 0.5 : 1} sx={{ flex: 1 }}>
                  {scopeItems.length > 0 ? (
                    scopeItems.map((item, index) => (
                      <Grid item xs={isSmallMobile ? 12 : 6} key={index}>
                        <KPICard
                          title={item.product_type}
                          value={item.quantity}
                          icon={<AssignmentIcon sx={{ color: '#F79233' }} />}
                          height={isSmallMobile ? "60px" : isMobile ? "100px" : "150px"}
                        />
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: isSmallMobile ? '100px' : '150px',
                        color: '#666',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        p: 1
                      }}>
                        <Typography
                          variant={isSmallMobile ? "caption" : "body2"}
                          className="responsive-text"
                        >
                          No scope of work defined for this corridor
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleAssignClose}
      >
        {fieldOfficerAssignments[corridor.corridor_id] && (
          <>
            <MenuItem
              onClick={handleDeleteAssignment}
              sx={{ color: 'error.main' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloseIcon fontSize="small" />
                Remove Assignment
              </Box>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                Or reassign to:
              </Typography>
            </MenuItem>
          </>
        )}
        {fieldOfficers.map((officer) => (
          <MenuItem
            key={officer._id}
            onClick={() => handleAssignFieldOfficer(officer._id)}
          >
            {officer.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Confirmation Dialog for Reassignment */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelReassignment}
        {...(isMobile ? {
          fullScreen: isSmallMobile,
          maxWidth: 'xs',
          fullWidth: true,
          PaperProps: {
            sx: {
              margin: isSmallMobile ? 0 : 2,
              width: isSmallMobile ? '100%' : 'auto'
            }
          }
        } : {
          maxWidth: "sm",
          fullWidth: true
        })}
      >
        <DialogTitle sx={{ p: isSmallMobile ? 2 : 3 }}>
          <Typography
            variant={isSmallMobile ? "h6" : "h5"}
            className="responsive-text"
          >
            Confirm Field Officer Reassignment
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: isSmallMobile ? 2 : 3 }}>
          <Typography
            variant={isSmallMobile ? "body2" : "body1"}
            className="responsive-text"
          >
            This corridor already has a field officer assigned ({fieldOfficerAssignments[corridor.corridor_id]}).
            Are you sure you want to reassign it to <strong>{confirmDialog.fieldOfficerName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: isSmallMobile ? 2 : 3,
          gap: isSmallMobile ? 1 : 0,
          flexDirection: isSmallMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={handleCancelReassignment}
            color="inherit"
            fullWidth={isSmallMobile}
            size={isSmallMobile ? "medium" : "large"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReassignment}
            variant="contained"
            fullWidth={isSmallMobile}
            size={isSmallMobile ? "medium" : "large"}
            sx={{
              bgcolor: '#F79233',
              '&:hover': { bgcolor: '#F79233', opacity: 0.9 }
            }}
          >
            Confirm Reassignment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CorridorDashboard; 
